import { useMemo, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Copy, Eye, MessageCircle, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";

const EMPTY = {
  name: "", category: "General", outlet_id: "", subject: "", scheduled_for: "",
  article_ids: [], candidate_ids: [], banner_ids: [],
};

export default function Boletines() {
  const { data, mutate } = useSWR("/campaigns", fetcher, { refreshInterval: 20000 });
  const { data: outlets } = useSWR("/outlets", fetcher);
  const { data: banners } = useSWR("/banners", fetcher);
  const { data: articlesData } = useSWR("/articles?limit=100&status=published", fetcher);
  const articles = articlesData?.items;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);

  const showMessaging = async (campaign) => {
    setBusy(`m-${campaign.id}`);
    try {
      const { data: m } = await api.post(`/campaigns/${campaign.id}/messaging/preview`);
      setMsg({ ...m, campaignId: campaign.id, text: m.text });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const sendMessaging = async (channel) => {
    setBusy(`send-${channel}`);
    try {
      const { data: r } = await api.post(`/campaigns/${msg.campaignId}/messaging/send`, {
        channels: [channel], text_override: msg.text,
      });
      if (r.sent.length) toast.success(`Boletín enviado por ${channel} (${r.sent.length} destino/s)`);
      (r.errors || []).forEach((e) => toast.error(`${e.channel}: ${e.error}`));
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, id) =>
    set(k, (form[k] || []).includes(id) ? form[k].filter((x) => x !== id) : [...(form[k] || []), id]);
  const outletName = (id) => (outlets || []).find((o) => o.id === id)?.name || "Todos los medios";

  const generate = async () => {
    setBusy("gen");
    try {
      const { data: r } = await api.post("/campaigns/generate");
      if (r.created) toast.success(`${r.created} borrador(es) creado(s), uno por medio`);
      (r.skipped || []).forEach((s) => toast.info(`${s.outlet}: ${s.reason}`));
      if (!r.created && !r.skipped?.length) toast.info("No hay medios activos");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const payload = {
        ...form,
        outlet_id: form.outlet_id || null,
        scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
      };
      if (form.id) await api.put(`/campaigns/${form.id}`, payload);
      else await api.post("/campaigns", payload);
      toast.success(form.scheduled_for ? "Boletín programado" : "Boletín guardado");
      setOpen(false);
      setForm(EMPTY);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const showPreview = async (id) => {
    setBusy(`p-${id}`);
    try {
      const { data: p } = await api.post(`/campaigns/${id}/preview`);
      setPreview(p);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const send = async (id) => {
    setBusy(id);
    try {
      const { data: stats } = await api.post(`/campaigns/${id}/send`);
      toast.success(`Enviado a ${stats.sent} de ${stats.recipients} suscriptores`);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  // Al editar un borrador generado se muestran solo sus noticias candidatas.
  const candidateList = useMemo(() => ((form.candidate_ids || []).length
    ? (articles || []).filter((a) => form.candidate_ids.includes(a.id))
    : (articles || []).filter((a) => !form.outlet_id || (a.outlet_ids || []).includes(form.outlet_id)).slice(0, 25)),
  [articles, form.candidate_ids, form.outlet_id]);

  return (
    <div>
      <PageHeader
        kicker="Email marketing"
        title="Boletines por medio"
        description="Un boletín por cada medio, con las noticias publicadas desde el último envío, su logo, sus redes y anuncios patrocinados."
      >
        <Button variant="outline" onClick={generate} disabled={busy === "gen"} data-testid="generate-campaigns-btn">
          <Sparkles className="h-4 w-4 mr-2" />
          {busy === "gen" ? "Generando…" : "Generar borradores por medio"}
        </Button>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-campaign-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo boletín
        </Button>
      </PageHeader>

      <div className="border border-border bg-card divide-y divide-border" data-testid="campaigns-list">
        {(data || []).map((c) => (
          <div key={c.id} className="p-5 flex flex-col md:flex-row gap-3 justify-between" data-testid={`campaign-${c.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge variant={c.status === "sent" ? "default" : c.status === "error" ? "destructive" : "secondary"}>
                  {c.status === "sent" ? "Enviado" : c.status === "scheduled" ? "Programado" : c.status === "error" ? "Error" : "Borrador"}
                </Badge>
                <span className="kicker">{outletName(c.outlet_id)}</span>
                <span className="text-xs text-muted-foreground">
                  {(c.article_ids || []).length} noticias
                  {c.candidate_ids?.length ? ` de ${c.candidate_ids.length}` : ""}
                  {c.banner_ids?.length ? ` · ${c.banner_ids.length} anuncio(s)` : ""}
                </span>
              </div>
              <h3 className="text-lg font-semibold">{c.name}</h3>
              <p className="text-sm text-muted-foreground">{c.subject || "Asunto generado por IA al enviar"}</p>
              <div className="text-xs mono text-muted-foreground mt-1">
                {c.scheduled_for ? `Programado: ${c.scheduled_for.slice(0, 16).replace("T", " ")}` : "Sin programar"}
                {c.stats?.sent ? ` · ${c.stats.sent}/${c.stats.recipients} enviados` : ""}
              </div>
              {c.errors?.length > 0 && <p className="text-xs text-destructive mt-1">{c.errors[0]}</p>}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0 items-start">
              <Button size="sm" variant="outline" onClick={() => showPreview(c.id)} disabled={busy === `p-${c.id}`} data-testid={`preview-campaign-${c.id}`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" />{busy === `p-${c.id}` ? "Generando…" : "Previsualizar"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setForm({ ...EMPTY, ...c, outlet_id: c.outlet_id || "", scheduled_for: (c.scheduled_for || "").slice(0, 16) }); setOpen(true); }} data-testid={`edit-campaign-${c.id}`}>
                Revisar noticias
              </Button>
              <Button size="sm" variant="outline" onClick={() => showMessaging(c)} disabled={busy === `m-${c.id}`} data-testid={`messaging-campaign-${c.id}`}>
                <MessageCircle className="h-3.5 w-3.5 mr-1.5" />{busy === `m-${c.id}` ? "Armando…" : "WhatsApp / Telegram"}
              </Button>
              <Button size="sm" onClick={() => send(c.id)} disabled={busy === c.id} data-testid={`send-campaign-${c.id}`}>
                <Send className="h-3.5 w-3.5 mr-1.5" />{busy === c.id ? "Enviando…" : "Enviar"}
              </Button>
              <Button size="sm" variant="outline" onClick={async () => { await api.delete(`/campaigns/${c.id}`); mutate(); }} data-testid={`delete-campaign-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {data?.length === 0 && (
          <p className="text-sm text-muted-foreground p-8 text-center">
            Aún no hay boletines. Pulse «Generar borradores por medio» para crear uno por cada medio activo.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Boletín</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider">Nombre interno</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="campaign-name-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Medio</Label>
              <select className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                value={form.outlet_id || ""} onChange={(e) => set("outlet_id", e.target.value)} data-testid="campaign-outlet-select">
                <option value="">Todos los medios</option>
                {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Categoría</Label>
                <Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5" data-testid="campaign-category-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Programar envío</Label>
                <Input type="datetime-local" value={form.scheduled_for || ""} onChange={(e) => set("scheduled_for", e.target.value)} className="mt-1.5" data-testid="campaign-schedule-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Asunto (opcional, la IA lo genera)</Label>
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" data-testid="campaign-subject-input" /></div>

            <div className="border-t border-border pt-3">
              <Label className="text-xs uppercase tracking-wider">
                Noticias del boletín ({(form.article_ids || []).length} seleccionadas)
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Desmarque las que no desea incluir en este envío.
              </p>
              <div className="space-y-2 mt-2 max-h-56 overflow-y-auto pr-1">
                {candidateList.map((a) => (
                  <label key={a.id} className="flex items-start gap-3 text-sm cursor-pointer">
                    <Checkbox
                      checked={(form.article_ids || []).includes(a.id)}
                      onCheckedChange={() => toggle("article_ids", a.id)}
                      data-testid={`campaign-article-${a.id}`}
                    />
                    <span className="line-clamp-2">{a.title}</span>
                  </label>
                ))}
                {candidateList.length === 0 && (
                  <p className="text-xs text-muted-foreground">Sin noticias candidatas para este medio.</p>
                )}
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <Label className="text-xs uppercase tracking-wider">Anuncios a incluir</Label>
              <p className="text-xs text-muted-foreground mt-1">
                El primero se coloca tras la nota principal; los demás, al final.
              </p>
              <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                {(banners || []).filter((b) => b.active).map((b) => (
                  <label key={b.id} className="flex items-center gap-3 text-sm cursor-pointer">
                    <Checkbox
                      checked={(form.banner_ids || []).includes(b.id)}
                      onCheckedChange={() => toggle("banner_ids", b.id)}
                      data-testid={`campaign-banner-${b.id}`}
                    />
                    <span className="truncate">{b.name} · {b.advertiser || "sin anunciante"}</span>
                  </label>
                ))}
                {(banners || []).filter((b) => b.active).length === 0 && (
                  <p className="text-xs text-muted-foreground">No hay anuncios activos.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.name || busy === "save"} data-testid="save-campaign-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="serif text-2xl">Previsualización del boletín</DialogTitle></DialogHeader>
          {preview && (
            <>
              <div className="border border-border p-4 text-sm space-y-1">
                <div><span className="kicker">Asunto</span><p className="font-medium" data-testid="preview-subject">{preview.subject}</p></div>
                {preview.preheader && <p className="text-xs text-muted-foreground">{preview.preheader}</p>}
                <p className="text-xs mono text-muted-foreground pt-1">
                  {preview.outlet ? `${preview.outlet} · ` : ""}{preview.articles} noticias · {preview.banners} anuncios · {preview.recipients} destinatarios
                </p>
              </div>
              <iframe
                title="Previsualización"
                srcDoc={preview.html}
                className="w-full h-[540px] border border-border bg-white"
                data-testid="preview-frame"
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!msg} onOpenChange={(o) => !o && setMsg(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="messaging-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Boletín por WhatsApp y Telegram</DialogTitle>
          </DialogHeader>
          {msg && (
            <div className="space-y-3">
              <p className="text-xs mono text-muted-foreground">
                {msg.outlet} · {msg.articles} titulares con enlace y suscripción a los canales
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant={msg.telegram_ready ? "default" : "secondary"}>
                  Telegram {msg.telegram_ready ? "listo" : "sin configurar"}
                </Badge>
                <Badge variant={msg.whatsapp_ready ? "default" : "secondary"}>
                  WhatsApp {msg.whatsapp_ready
                    ? `listo (${(msg.whatsapp_recipients || []).length} destino/s)`
                    : "sin configurar"}
                </Badge>
              </div>
              <textarea
                value={msg.text}
                onChange={(e) => setMsg({ ...msg, text: e.target.value })}
                rows={16}
                className="w-full border border-input bg-background p-3 text-sm mono rounded-md"
                data-testid="messaging-text"
              />
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={async () => {
                  await navigator.clipboard.writeText(msg.text);
                  toast.success("Texto copiado");
                }} data-testid="copy-messaging-btn">
                  <Copy className="h-3.5 w-3.5 mr-1.5" />Copiar texto
                </Button>
                <Button onClick={() => sendMessaging("telegram")}
                  disabled={!msg.telegram_ready || busy === "send-telegram"}
                  data-testid="send-telegram-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {busy === "send-telegram" ? "Enviando…" : "Enviar por Telegram"}
                </Button>
                <Button onClick={() => sendMessaging("whatsapp")}
                  disabled={!msg.whatsapp_ready || busy === "send-whatsapp"}
                  data-testid="send-whatsapp-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {busy === "send-whatsapp" ? "Enviando…" : "Enviar por WhatsApp"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Los canales de WhatsApp no admiten envío por API: pegue el texto copiado en el canal, o
                configure destinatarios en Medios → Boletín por WhatsApp y Telegram para el envío directo.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
